import React, { useState, useEffect } from 'react';
import { FaBriefcase, FaExclamationTriangle, FaSitemap, FaUsers, FaLayerGroup, FaUserTie, FaCalendarAlt, FaFlag } from 'react-icons/fa';
import axiosInstance from '../utils/axiosConfig';
import { Modal } from './common';
import { MOTIVOS } from './vacantesShared';
import './AddVacanteModal.css';
import './FormModal.css';
import './VacantesUI.css';

const CARGO_NUEVO = '__nuevo__';

const NIVELES = [
  [10, 'Presidente'], [20, 'Gerente General'], [30, 'Gerente'], [31, 'Gerente Nacional'],
  [32, 'Gerente Regional'], [40, 'Líder'], [50, 'Coordinador'], [51, 'Coordinador de Venta'],
  [60, 'Especialista'], [70, 'Analista'], [80, 'Ayudante'], [90, 'Pasante'],
];

const hoyISO = () => {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
};

/**
 * Abrir o editar una vacante: el puesto que hay que cubrir.
 * El motivo dice por qué existe; el cargo se elige del área o se crea.
 * `onSuccess` recibe la vacante guardada.
 */
const AddVacanteModal = ({ vacante, onClose, onSuccess }) => {
  const isEditing = !!vacante;

  const [formData, setFormData] = useState({
    motivo: vacante?.motivo && MOTIVOS.some((m) => m.valor === vacante.motivo) ? vacante.motivo : 'Reemplazo',
    gerencia_id: vacante?.gerencia_id || '',
    departamento_id: vacante?.departamento_id || '',
    area_id: vacante?.area_id || '',
    cargo_area_id: vacante?.cargo_area_id || '',
    nuevo_cargo_nombre: '',
    nuevo_cargo_nivel: '',
    titulo: vacante?.titulo || '',
    supervisor_id: vacante?.supervisor_id || '',
    prioridad: vacante?.prioridad || 'Media',
    fecha_creacion: vacante?.fecha_creacion || hoyISO(),
    fecha_estimada_cierre: vacante?.fecha_estimada_cierre || '',
    descripcion: vacante?.descripcion || '',
  });

  const [gerencias, setGerencias] = useState([]);
  const [departamentos, setDepartamentos] = useState([]);
  const [areas, setAreas] = useState([]);
  const [cargos, setCargos] = useState([]);
  const [supervisores, setSupervisores] = useState({ empleados: [], vacantes: [] });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const cargarDepartamentos = (gerenciaId) =>
    axiosInstance.get(`/api/departamentos/${gerenciaId}`).then((r) => setDepartamentos(r.data));
  const cargarAreas = (departamentoId) =>
    axiosInstance.get(`/api/areas/${departamentoId}`).then((r) => setAreas(r.data));
  const cargarCargos = (areaId) =>
    axiosInstance.get(`/api/cargos/${areaId}`).then((r) => setCargos(
      [...r.data].sort((a, b) => a.nombre.localeCompare(b.nombre, 'es', { sensitivity: 'base' }))
    ));

  useEffect(() => {
    const ordenar = (lista) => lista.sort((a, b) => a.nombre.localeCompare(b.nombre, 'es', { sensitivity: 'base' }));
    Promise.all([
      axiosInstance.get('/api/gerencias'),
      axiosInstance.get('/api/empleados'),
      axiosInstance.get('/api/vacantes'),
    ]).then(([gerenciasRes, empleadosRes, vacantesRes]) => {
      setGerencias(ordenar([...gerenciasRes.data]));
      setSupervisores({
        empleados: ordenar(empleadosRes.data.map((emp) => ({
          id: emp.id, nombre: emp.nombre, detalle: emp.cargo || 'Sin cargo',
        }))),
        // Un puesto vacante tambien puede tener gente debajo
        vacantes: ordenar(vacantesRes.data
          .filter((v) => !['Cubierta', 'Cancelada'].includes(v.estado) && v.id !== vacante?.id)
          .map((v) => ({ id: v.id, nombre: v.titulo, detalle: 'vacante' }))),
      });
    }).catch((err) => console.error('Error cargando opciones de vacante:', err));

    if (vacante) {
      if (vacante.gerencia_id) cargarDepartamentos(vacante.gerencia_id);
      if (vacante.departamento_id) cargarAreas(vacante.departamento_id);
      if (vacante.area_id) cargarCargos(vacante.area_id);
    }
  }, [vacante]);

  const handleChange = (e) => {
    const { name, value } = e.target;

    if (name === 'gerencia_id') {
      setFormData((prev) => ({ ...prev, gerencia_id: value, departamento_id: '', area_id: '', cargo_area_id: '' }));
      setDepartamentos([]); setAreas([]); setCargos([]);
      if (value) cargarDepartamentos(value);
      return;
    }
    if (name === 'departamento_id') {
      setFormData((prev) => ({ ...prev, departamento_id: value, area_id: '', cargo_area_id: '' }));
      setAreas([]); setCargos([]);
      if (value) cargarAreas(value);
      return;
    }
    if (name === 'area_id') {
      setFormData((prev) => ({ ...prev, area_id: value, cargo_area_id: '' }));
      setCargos([]);
      if (value) cargarCargos(value);
      return;
    }
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const esCargoNuevo = formData.cargo_area_id === CARGO_NUEVO;
  const cargoElegido = cargos.find((c) => String(c.id) === String(formData.cargo_area_id));
  const tituloSugerido = esCargoNuevo ? formData.nuevo_cargo_nombre : cargoElegido?.nombre;

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');

    const payload = {
      motivo: formData.motivo,
      area_id: formData.area_id,
      titulo: formData.titulo.trim() || tituloSugerido || '',
      supervisor_id: formData.supervisor_id || null,
      prioridad: formData.prioridad,
      fecha_creacion: formData.fecha_creacion,
      fecha_estimada_cierre: formData.fecha_estimada_cierre || null,
      descripcion: formData.descripcion,
    };
    if (esCargoNuevo) {
      payload.nuevo_cargo_nombre = formData.nuevo_cargo_nombre.trim();
      payload.nuevo_cargo_nivel = formData.nuevo_cargo_nivel;
    } else {
      payload.cargo_area_id = formData.cargo_area_id;
    }

    try {
      const response = isEditing
        ? await axiosInstance.patch(`/api/vacantes/${vacante.id}`, payload)
        : await axiosInstance.post('/api/vacantes', payload);
      onSuccess(response.data.vacante);
    } catch (err) {
      setError(err.response?.data?.error || 'No se pudo guardar la vacante');
      console.error('Error saving vacante:', err);
    } finally {
      setLoading(false);
    }
  };

  return (
    <Modal isOpen={true} onClose={onClose} size="lg">
      <Modal.Header onClose={onClose}>
        <span className="flex items-center gap-2">
          <FaBriefcase />
          {isEditing ? 'Editar vacante' : 'Abrir vacante'}
        </span>
      </Modal.Header>
      <Modal.Body>
        {error && (
          <div className="form-modal-error mb-4">
            <FaExclamationTriangle />
            {error}
          </div>
        )}

        <form onSubmit={handleSubmit} id="vacante-form">
          <div className="form-modal-section">
            <h3><FaFlag /> ¿Por qué se abre?</h3>
            <div className="vac-segmentado" role="radiogroup" aria-label="Motivo">
              {MOTIVOS.map((m) => (
                <button
                  type="button"
                  key={m.valor}
                  role="radio"
                  aria-checked={formData.motivo === m.valor}
                  className={`vac-segmento${formData.motivo === m.valor ? ' vac-segmento--activo' : ''}`}
                  onClick={() => setFormData((prev) => ({ ...prev, motivo: m.valor }))}
                >
                  <span className="vac-segmento-titulo">{m.valor}</span>
                  <span className="vac-segmento-detalle">{m.detalle}</span>
                </button>
              ))}
            </div>
          </div>

          <div className="form-modal-section">
            <h3><FaSitemap /> Puesto</h3>
            <div className="form-modal-grid">
              <div className="form-modal-group">
                <label htmlFor="vac-gerencia"><FaSitemap className="input-icon" /> Gerencia</label>
                <select id="vac-gerencia" name="gerencia_id" value={formData.gerencia_id} onChange={handleChange} required>
                  <option value="">Seleccione gerencia</option>
                  {gerencias.map((g) => <option key={g.id} value={g.id}>{g.nombre}</option>)}
                </select>
              </div>

              <div className="form-modal-group">
                <label htmlFor="vac-depto"><FaUsers className="input-icon" /> Departamento</label>
                <select id="vac-depto" name="departamento_id" value={formData.departamento_id} onChange={handleChange} disabled={!formData.gerencia_id} required>
                  <option value="">Seleccione departamento</option>
                  {departamentos.map((d) => <option key={d.id} value={d.id}>{d.nombre}</option>)}
                </select>
              </div>

              <div className="form-modal-group">
                <label htmlFor="vac-area"><FaLayerGroup className="input-icon" /> Área</label>
                <select id="vac-area" name="area_id" value={formData.area_id} onChange={handleChange} disabled={!formData.departamento_id} required>
                  <option value="">Seleccione área</option>
                  {areas.map((a) => <option key={a.id} value={a.id}>{a.nombre}</option>)}
                </select>
              </div>

              <div className="form-modal-group">
                <label htmlFor="vac-cargo"><FaBriefcase className="input-icon" /> Cargo</label>
                <select id="vac-cargo" name="cargo_area_id" value={formData.cargo_area_id} onChange={handleChange} disabled={!formData.area_id} required>
                  <option value="">Seleccione cargo</option>
                  {cargos.map((c) => <option key={c.id} value={c.id}>{c.nombre}</option>)}
                  <option value={CARGO_NUEVO}>Otro cargo (no está en la lista)</option>
                </select>
              </div>

              {esCargoNuevo && (
                <>
                  <div className="form-modal-group">
                    <label htmlFor="vac-cargo-nombre"><FaBriefcase className="input-icon" /> Nombre del cargo</label>
                    <input
                      id="vac-cargo-nombre"
                      type="text"
                      name="nuevo_cargo_nombre"
                      value={formData.nuevo_cargo_nombre}
                      onChange={handleChange}
                      placeholder="Ej: Líder de Procura"
                      required
                    />
                  </div>
                  <div className="form-modal-group">
                    <label htmlFor="vac-cargo-nivel"><FaLayerGroup className="input-icon" /> Nivel del cargo</label>
                    <select id="vac-cargo-nivel" name="nuevo_cargo_nivel" value={formData.nuevo_cargo_nivel} onChange={handleChange} required>
                      <option value="">Seleccione nivel</option>
                      {NIVELES.map(([n, nombre]) => <option key={n} value={n}>{n} - {nombre}</option>)}
                    </select>
                  </div>
                </>
              )}

              <div className="form-modal-group span-full">
                <label htmlFor="vac-supervisor"><FaUserTie className="input-icon" /> Reporta a</label>
                <select id="vac-supervisor" name="supervisor_id" value={formData.supervisor_id} onChange={handleChange} required>
                  <option value="">Seleccione a quién reporta</option>
                  <optgroup label="Empleados">
                    {supervisores.empleados.map((s) => (
                      <option key={s.id} value={s.id}>{s.nombre} - {s.detalle}</option>
                    ))}
                  </optgroup>
                  {supervisores.vacantes.length > 0 && (
                    <optgroup label="Puestos vacantes">
                      {supervisores.vacantes.map((s) => (
                        <option key={s.id} value={s.id}>{s.nombre}</option>
                      ))}
                    </optgroup>
                  )}
                </select>
              </div>

              <div className="form-modal-group span-full">
                <label htmlFor="vac-titulo"><FaBriefcase className="input-icon" /> Nombre de la vacante</label>
                <input
                  id="vac-titulo"
                  type="text"
                  name="titulo"
                  value={formData.titulo}
                  onChange={handleChange}
                  placeholder={tituloSugerido || 'Por defecto, el nombre del cargo'}
                />
                <span className="form-modal-input-hint">
                  Opcional. Úsalo para distinguir puestos iguales, por ejemplo: Coordinador de Ventas Occidente.
                </span>
              </div>
            </div>
          </div>

          <div className="form-modal-section">
            <h3><FaCalendarAlt /> Seguimiento</h3>
            <div className="form-modal-grid three-cols">
              <div className="form-modal-group">
                <label htmlFor="vac-prioridad">Prioridad</label>
                <select id="vac-prioridad" name="prioridad" value={formData.prioridad} onChange={handleChange} required>
                  <option value="Alta">Alta</option>
                  <option value="Media">Media</option>
                  <option value="Baja">Baja</option>
                </select>
              </div>
              <div className="form-modal-group">
                <label htmlFor="vac-apertura">Abierta desde</label>
                <input id="vac-apertura" type="date" name="fecha_creacion" value={formData.fecha_creacion} onChange={handleChange} required />
              </div>
              <div className="form-modal-group">
                <label htmlFor="vac-meta">Cubrir antes de</label>
                <input id="vac-meta" type="date" name="fecha_estimada_cierre" value={formData.fecha_estimada_cierre} onChange={handleChange} />
              </div>
              <div className="form-modal-group span-full three-cols">
                <label htmlFor="vac-notas">Notas</label>
                <textarea
                  id="vac-notas"
                  name="descripcion"
                  value={formData.descripcion}
                  onChange={handleChange}
                  rows="3"
                  placeholder="Perfil buscado, contexto, a quién reemplaza..."
                />
              </div>
            </div>
          </div>
        </form>
      </Modal.Body>
      <Modal.Footer>
        <button type="button" className="btn btn-secondary" onClick={onClose}>
          Cancelar
        </button>
        <button type="submit" form="vacante-form" className="btn btn-primary" disabled={loading}>
          {loading ? 'Guardando...' : (isEditing ? 'Guardar cambios' : 'Abrir vacante')}
        </button>
      </Modal.Footer>
    </Modal>
  );
};

export default AddVacanteModal;
